import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Container,
  Image,
  Nav,
  Row,
  Tab,
  Modal,
} from "react-bootstrap";
import { IMAGES } from "../../../assets/images/images";
import {
  updateProfile,
  changePassword,
  addProfilePicture,
  getUserDetail,
} from "../../../utility/apiServices/userServices";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import Cropper from "react-easy-crop";
import Slider from "@mui/material/Slider";
import { phoneRegExp } from "../../../utility/local/constants";
import { useTranslation } from "react-i18next";



const UserProfileSettings = () => {
  const { t } = useTranslation()
  const profileImg = localStorage.getItem("profile_pic") || null;
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppingModalVisible, setCroppingModalVisible] = useState(false);
  const [profileURL, setprofileURL] = useState(profileImg);
  const [Uploading, setUploading] = useState(false);


  const ProfileSchema = Yup.object().shape({
    name: Yup.string().required(t("nameRequired")),
    email: Yup.string()
      .email(t("invalidEmailAddress"))
      .required(t("emailRequired")),
    phone: Yup.string()
      .required(t("phoneNumberRequired"))
      .min(10)
      .max(20)
      .matches(phoneRegExp, t("phoneNumberInvalid"))
      .label(t("phoneNumber")),
  });

  const PasswordSchema = Yup.object().shape({
    password: Yup.string()
      .required(t("currentPasswordRequired"))
      .label(t("currentPassword")),
    newPassword: Yup.string()
      .min(8, t("minPasswordLength"))
      .required(t("newPasswordRequired"))
      .label(t("newPassword")),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], t("passwordsMustMatch"))
      .required(t("confirmNewPasswordRequired"))
      .min(8, t("minPasswordLength"))
      .label(t("confirmNewPassword")),
  });
  const initialPasswordValues = {
    password: "",
    newPassword: "",
    confirmNewPassword: "",
  };
  const [initialProfileValues, setInitialProfileValues] = useState({
    name: '',
    email: '',
    phone: '',
  });

  const getMyDetail = async () => {
    try {
      const detail = await getUserDetail();
      const { name, email, phone_number, profile_url } = detail?.data || {};

      setInitialProfileValues({
        name: name || '',
        email: email || '',
        phone: phone_number || '',
      });

      // Update profile URL if it exists in the response
      if (profile_url) {
        setprofileURL(profile_url);
        localStorage.setItem("profile_pic", profile_url);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  useEffect(() => {
    getMyDetail();
  }, []);


  const handleProfileSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await updateProfile({
        name: values.name,
        email: values.email,
        phone_number: values.phone,
      });
      if (response?.status) {
        Swal.fire({
          icon: "success",
          text: response?.message,
        });
        localStorage.setItem("name", response?.user?.name);
        localStorage.setItem("email", response?.user?.email);
        localStorage.setItem("phone", response?.user?.phone_number);
      } else {
        Swal.fire({
          icon: "error",
          text: response?.message,
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        text: error?.message,
      });
    }
    setSubmitting(false);
  };

  const handlePasswordSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const response = await changePassword({
        currentPassword: values.password,
        newPassword: values.newPassword,
      });
      if (response?.status) {
        Swal.fire({
          icon: "success",
          text: response?.message,
        });
      } else {
        Swal.fire({
          icon: "error",
          text: response?.message,
        });
      }
      resetForm();
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        text: error?.message || t("somethingWentWrong"),
      });
    }
    setSubmitting(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setSelectedImage(reader.result);
        setCroppingModalVisible(true);
      };
    }
  };

  const handleCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const cropImage = () => {
    return new Promise((resolve) => {
      const image = new window.Image();
      image.src = selectedImage;
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const { width, height } = croppedAreaPixels;
        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          width,
          height,
          0,
          0,
          width,
          height
        );

        canvas.toBlob((blob) => {
          resolve(blob);
        }, "image/jpeg");
      };
    });
  };

  const handleUploadCroppedImage = async () => {
    const croppedBlob = await cropImage();
    const formData = new FormData();
    // Add filename with proper extension so S3 saves it correctly
    formData.append("image", croppedBlob, "profile-picture.jpg");

    try {
      setUploading(true);
      const response = await addProfilePicture(formData);

      if (response?.status) {
        Swal.fire({
          icon: "success",
          text: t("profileImageUpdatedSuccessfully"),
        });
        setprofileURL(response?.url);
        localStorage.setItem("profile_pic", response?.url);
      } else {
        console.error("Upload failed - Response:", response);
        Swal.fire({
          icon: "error",
          text: response?.message || t("failedToUpdateProfileImage"),
        });
      }
      setUploading(false);
    } catch (error) {
      console.error("Error updating profile image:", error);
      Swal.fire({
        icon: "error",
        text: t("errorUpdatingProfileImage"),
      });
      setUploading(false);
    }
    setCroppingModalVisible(false);
  };

  return (
    <React.Fragment>
      <section className="commonpad">
        <Container fluid>
          <h2>{t("profileSettings")}</h2>
          <Row className="mt-4">
            <Col lg={3}>
              <div className="profile_User">
                <div className="profile_UserPic">
                  <Image
                    src={
                      profileURL == null ||
                        profileURL == "null" ||
                        profileURL == "undefined" ||
                        profileURL == undefined
                        ? IMAGES.UserDisImage
                        : profileURL
                    }
                    alt=""
                  />
                </div>
                <Button
                  variant="outline-primary"
                  onClick={() => fileInputRef.current.click()}
                >
                  {t("choose")}
                </Button>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
            </Col>
            <Col lg={9}>
              <div className="tabProfile">
                <Tab.Container defaultActiveKey="first">
                  <Nav variant="pills">
                    <Nav.Item>
                      <Nav.Link eventKey="first">{t("basicInfo")}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="second">{t("changePassword")}</Nav.Link>
                    </Nav.Item>
                  </Nav>
                  <Tab.Content>
                    <Tab.Pane eventKey="first">
                      <Formik
                        enableReinitialize={true}
                        initialValues={initialProfileValues}
                        validationSchema={ProfileSchema}
                        onSubmit={handleProfileSubmit}
                      >
                        {({ isSubmitting, values }) => (
                          <Form>
                            <Row>
                              <Col md={6}>
                                <div className="mb-3">
                                  <label htmlFor="name">{t("name")}</label>
                                  <Field
                                    type="text"
                                    name="name"
                                    className="form-control"
                                    value={values.name}
                                    placeholder={t("enterYourName")}
                                  />
                                  <ErrorMessage
                                    name="name"
                                    component="div"
                                    className="errorStyle"
                                  />
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-3">
                                  <label htmlFor="email">{t("emailAddress")}</label>
                                  <Field
                                    disabled
                                    type="email"
                                    name="email"
                                    value={values.email}
                                    className="form-control"
                                    placeholder={t("enterEmailAddress")}
                                  />
                                  <ErrorMessage
                                    name="email"
                                    component="div"
                                    className="errorStyle"
                                  />
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-3">
                                  <label htmlFor="phone">{t("phoneNumber")}</label>
                                  <Field
                                    type="text"
                                    name="phone"
                                    value={values.phone}
                                    className="form-control"
                                    placeholder={t("enterPhoneNumber")}
                                  />
                                  <ErrorMessage
                                    name="phone"
                                    component="div"
                                    className="errorStyle"
                                  />
                                </div>
                              </Col>
                            </Row>
                            <Button
                              type="submit"
                              className="px-5 mt-2"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? t("updating") : t("update")}
                            </Button>
                          </Form>
                        )}
                      </Formik>
                    </Tab.Pane>
                    <Tab.Pane eventKey="second">
                      <Formik
                        initialValues={initialPasswordValues}
                        validationSchema={PasswordSchema}
                        onSubmit={handlePasswordSubmit}
                      >
                        {({ isSubmitting }) => (
                          <Form>
                            <Row>
                              <Col md={6}>
                                <div className="mb-3">
                                  <label htmlFor="password">
                                    {t("currentPassword")}
                                  </label>
                                  <Field
                                    type="password"
                                    name="password"
                                    className="form-control"
                                    placeholder={t("currentPassword")}
                                  />
                                  <ErrorMessage
                                    name="password"
                                    component="div"
                                    className="errorStyle"
                                  />
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-3">
                                  <label htmlFor="newPassword">
                                    {t("newPassword")}
                                  </label>
                                  <Field
                                    type="password"
                                    name="newPassword"
                                    className="form-control"
                                    // placeholder="New password"
                                    placeholder={t("newPassword")}
                                  />
                                  <ErrorMessage
                                    name="newPassword"
                                    component="div"
                                    className="errorStyle"
                                  />
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-3">
                                  <label htmlFor="confirmNewPassword">
                                    {t("confirmNewPassword")}
                                  </label>
                                  <Field
                                    type="password"
                                    name="confirmNewPassword"
                                    className="form-control"
                                    placeholder={t("confirmNewPassword")}
                                  />
                                  <ErrorMessage
                                    name="confirmNewPassword"
                                    component="div"
                                    className="errorStyle"
                                  />
                                </div>
                              </Col>
                            </Row>
                            <Button
                              type="submit"
                              className="px-5 mt-2"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? t("updating") : t("update")}
                            </Button>
                          </Form>
                        )}
                      </Formik>
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </div>
            </Col>
          </Row>
        </Container>

        {/* Cropping Modal */}
        <Modal
          show={croppingModalVisible}
          onHide={() => setCroppingModalVisible(false)}
          size="lg"
          centered
        >
          <Modal.Header>
            <Modal.Title>{t("cropImage")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div
              className="crop-container"
              style={{ position: "relative", width: "100%", height: "400px" }}
            >
              <Cropper
                image={selectedImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={handleCropComplete}
                onZoomChange={setZoom}
              />
            </div>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e, newZoom) => setZoom(newZoom)}
              style={{ marginTop: "20px" }}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setCroppingModalVisible(false)}
              disabled={Uploading}
            >
              {t("cancel")}
            </Button>
            <Button
              disabled={Uploading}
              variant="primary"
              onClick={handleUploadCroppedImage}
            >
              {Uploading ? <span className="spinner"></span> : t("upload")}
            </Button>
          </Modal.Footer>
        </Modal>
      </section>
    </React.Fragment>
  );
};

export default UserProfileSettings;
